import { UserServiceResponseModel, User, CreateUserServiceModel, UpdateUserServiceModel } from '../types';
import { detectFormChanges } from './changeDetector';

/**
 * Maps API user response to UI user model
 * Only maps essential fields: id, email, name, jobTitle, tenant, roles, status
 */
export const mapApiUserToUi = (apiUser: any): User => {
  // Map essential fields from actual API response structure
  const id = apiUser.id || '';
  const email = apiUser.email || '';
  
  // Combine firstName + lastName into name
  const firstName = apiUser.firstName || '';
  const lastName = apiUser.lastName || '';
  const name = `${firstName} ${lastName}`.trim() || 'Unknown User';
  
  // Handle status - API uses 'isActive' boolean field
  // Map boolean isActive to 'Active' | 'Inactive' status
  const status: 'Active' | 'Inactive' = apiUser.isActive === true || apiUser.isActive === 1 ? 'Active' : 'Inactive';
  
  // Handle subscription - use subscription.sponsorName from the subscription object
  const subscription = apiUser.subscription?.sponsorName || 
                      apiUser.subscription?.salesAgentName || 
                      apiUser.organization || // fallback to organization if no subscription field
                      'Unknown Subscription';
  console.log('🔧 Mapping subscription:', { 
    subscriptionObject: apiUser.subscription, 
    sponsorName: apiUser.subscription?.sponsorName,
    salesAgentName: apiUser.subscription?.salesAgentName,
    organization: apiUser.organization,
    mapped: subscription 
  });
  
  // Handle roles - API uses 'roleId' and 'roleName' fields
  const roles = apiUser.roleName ? [apiUser.roleName] : [];
  
  // Handle job title - directly available in API
  const jobTitle = apiUser.jobTitle || '';
  console.log('🔧 Mapping jobTitle:', { original: apiUser.jobTitle, mapped: jobTitle });
  
  // Handle dates - API uses 'startDate' for subscription start and 'lastLoginDate' for last login
  // Map startDate to createdAt for backward compatibility, but preserve original fields
  const createdAt = apiUser.startDate || apiUser.lastLoginDate || new Date();
  const updatedAt = apiUser.startDate || apiUser.lastLoginDate || new Date();
  
  return {
    id,
    email,
    name,
    jobTitle,
    tenant: subscription, // Map subscription to tenant field for backward compatibility
    roles,
    status,
    createdAt: new Date(createdAt),
    updatedAt: new Date(updatedAt),
  };
};

/**
 * Maps UI user form data to API create model
 */
export const mapUiToCreateUserApiModel = (formData: {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  subscription: string;
  role: string;
}): CreateUserServiceModel => {
  return {
    email: formData.email,
    firstName: formData.firstName,
    lastName: formData.lastName,
    subscriptionId: formData.subscription,
    roleId: formData.role, // Use single role directly
  };
};

/**
 * Maps UI user form data to API update model
 */
export const mapUiToUpdateUserApiModel = (formData: {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  subscription: string;
  role: string;
  status: 'Active' | 'Inactive';
}): UpdateUserServiceModel => {
  return {
    email: formData.email,
    firstName: formData.firstName,
    lastName: formData.lastName,
    subscriptionId: formData.subscription,
    roleId: formData.role, // Use single role directly
    isActive: formData.status === 'Active',
  };
};

/**
 * Maps UI user form data to API update model for PATCH (only changed fields)
 */
export const mapUiToUpdateUserApiModelPatch = (
  formData: {
    email: string;
    firstName: string;
    lastName: string;
    jobTitle: string;
    subscription: string;
    role: string;
    status: 'Active' | 'Inactive';
  },
  originalUser: User
): UpdateUserServiceModel => {
  // Convert original user to API format for comparison
  const originalNameParts = originalUser.name.trim().split(' ');
  const originalFirstName = originalNameParts[0] || '';
  const originalLastName = originalNameParts.slice(1).join(' ') || '';

  const originalApiModel: UpdateUserServiceModel = {
    email: originalUser.email,
    firstName: originalFirstName,
    lastName: originalLastName,
    subscriptionId: originalUser.tenant, // Assuming tenant field contains subscription info
    roleId: originalUser.roles[0] || '', // Use first role for single roleId
    isActive: originalUser.status === 'Active',
  };

  // Convert form data to API format
  const fullUpdate: UpdateUserServiceModel = {
    email: formData.email,
    firstName: formData.firstName,
    lastName: formData.lastName,
    subscriptionId: formData.subscription,
    roleId: formData.role, // Use single role directly
    isActive: formData.status === 'Active',
  };

  // Detect changes and return only modified fields
  return detectFormChanges(originalApiModel, fullUpdate);
};
